import type { Maybe } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { toCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import type { AppliedContract } from '../entities/AppliedContract.js';
import type { Contract } from '../entities/Contract.js';
import type { ContractSupplement } from '../entities/ContractSupplement.js';
import { getDateWithMostActiveSections, type SupplementDatesSelectionSet } from './getMinAndMaxDatesOfSupplement.js';

/** Contracts only ever start at the beginning or in the middle of a month. */
export const ALLOWED_EINSTELLUNGS_TAGE = [1, 15];

/** Changes end the day before the next one may start, so on the 14th or on the last day of a month. */
export const ALLOWED_ENDE_TAG = 14;

export const changeStartDateErrorMessage =
  'Das Datum muss der 1. oder der 15. eines Monats sein und darf nicht in der Vergangenheit liegen.';

export const changeEndDateErrorMessage =
  'Das Datum muss der 14. oder der letzte Tag eines Monats sein und darf weder in der Vergangenheit noch vor dem Beginn liegen.';

/** Short versions of the rules above, rendered next to the date fields so they are known before something is entered. */
export const changeStartDateHint = 'Nur der 1. oder 15. eines Monats';
export const changeEndDateHint = 'Nur der 14. oder letzte Tag eines Monats';

/** Whether a picked day is one the contract may start on. A canonical calendar day sits on UTC midnight, so the day of month is the UTC one. */
export function isAllowedEinstellungsdatum(date: Date): boolean {
  return ALLOWED_EINSTELLUNGS_TAGE.includes(toCalendarDay(date).getUTCDate());
}

/**
 * Whether a picked day is one a change may end on.
 *
 * Both the day and the length of its month are read in UTC. `endOfMonth` would not do: it reads the
 * canonical day through the browser's local getters, so west of Greenwich a 1 February would already
 * be 31 January there and the month would be measured as the wrong one.
 */
export function isAllowedEndOfChange(date: Date): boolean {
  const day = toCalendarDay(date);
  // day 0 of the following month is the last day of this one
  const lastDayOfMonth = new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth() + 1, 0)).getUTCDate();
  return day.getUTCDate() === ALLOWED_ENDE_TAG || day.getUTCDate() === lastDayOfMonth;
}

/** Whether a day lies before today, both read as canonical calendar days. */
export function isInThePast(date: Date): boolean {
  return toCalendarDay(date) < todayAsCalendarDay();
}

/**
 * The message for a start of a change that breaks the rules, `undefined` when the day is fine.
 * Used both for the Einstellungsdatum and for the start dates of a supplement.
 */
export function changeStartDateError(date: Maybe<Date>): string | undefined {
  if (!date) return undefined;
  if (!isAllowedEinstellungsdatum(date) || isInThePast(date)) return changeStartDateErrorMessage;
  return undefined;
}

/**
 * The message for an end of a change that breaks the rules, `undefined` when the day is fine.
 * @param minimum the start the end may not fall below, e.g. the start of the same change.
 */
export function changeEndDateError(date: Maybe<Date>, minimum?: Maybe<Date>): string | undefined {
  if (!date) return undefined;
  if (!isAllowedEndOfChange(date) || isInThePast(date) || (!!minimum && toCalendarDay(date) < toCalendarDay(minimum)))
    return changeEndDateErrorMessage;
  return undefined;
}

export function contractStartDate(
  contract: NarrowedEntity<Contract | AppliedContract, { createdAt: 1; agInfos: { einstellungsdatum: 1 } }>,
): Date | null {
  return contract.agInfos?.einstellungsdatum ?? null;
}

/** The supplement fields the runtime rules read. Optional everywhere: without them the notice date alone decides. */
export type ContractWithSupplementRuntimes = NarrowedEntity<
  Contract,
  { supplements: SupplementDatesSelectionSet & { automaticallyAssignedEndDateOfContract: 1 } }
>;

/** The selection set behind {@link ContractWithSupplementRuntimes}, for callers that request a contract themselves. */
export const SUPPLEMENT_RUNTIMES_SELECTION = {
  signedAt: 1,
  startDateGeneral: 1,
  endDateGeneral: 1,
  startDateAG1: 1,
  endDateAG1: 1,
  startDateAG2: 1,
  endDateAG2: 1,
  changes: { path: 1 },
  automaticallyAssignedEndDateOfContract: 1,
} as const satisfies BaseQLSelectionSet<ContractSupplement>;

/**
 * Runtimes of the supplements that were not ended together with the contract and that have already begun at the
 * given day. Such a supplement opens the contract again for its own runtime, so it can run past the notice date.
 */
function startedOpeningRuntimes(contract: Partial<ContractWithSupplementRuntimes>, day: Date) {
  // an entity from the network throws instead of returning undefined for a field that was not requested, so ask
  // it first - the supplements are optional here and the notice date alone decides without them
  if (contract.has?.('supplements') === false) return [];

  return (contract.supplements ?? [])
    .filter(s => !s.automaticallyAssignedEndDateOfContract)
    .map(s => getDateWithMostActiveSections(s))
    .filter(({ start }) => !!start && start <= day);
}

/**
 * Whether such an opening supplement is in effect on the given day, which keeps a contract running past its
 * notice date. The same reset `generateAppliedContract` applies to the Befristung, evaluated on the raw contract.
 */
export function isContractOpenedBySupplement(contract: Partial<ContractWithSupplementRuntimes>, day: Date): boolean {
  return startedOpeningRuntimes(contract, day).some(({ end }) => !end || end >= day);
}

/**
 * Contract runtimes are inclusive canonical calendar days: `einstellungsdatum` is the first working
 * day, `gekuendigtZum` the last. The reference instant is normalized to its German calendar day,
 * then everything compares as plain instants.
 */
export function isContractActive(
  time: Date,
  contract: NarrowedEntity<
    Contract | AppliedContract,
    {
      _id: 1;
      createdAt: 1;
      agInfos: { einstellungsdatum: 1; gekuendigtZum: 1; unterzeichnungsdatum: 1 };
    }
  > &
    Partial<ContractWithSupplementRuntimes>,
  ignoreSignature = false,
): boolean {
  const day = toCalendarDay(time);

  const start = contractStartDate(contract);
  if (!start && !ignoreSignature) return false;

  const end = contract.agInfos?.gekuendigtZum ?? null;

  return (
    !!(contract.agInfos?.unterzeichnungsdatum || ignoreSignature) &&
    (!start || start <= day) &&
    (!end || end >= day || isContractOpenedBySupplement(contract, day))
  );
}

/**
 * The last day of a contract as far as it is known today: its `gekuendigtZum`, or the end of a supplement that has
 * begun and runs past it. `null` while the contract has no end - no notice date, or a begun supplement without one.
 * A supplement that has not begun yet does not count until it does.
 */
export function contractEndDate(
  contract: NarrowedEntity<Contract | AppliedContract, { agInfos: { gekuendigtZum: 1 } }> &
    Partial<ContractWithSupplementRuntimes>,
  today: Date = todayAsCalendarDay(),
): Date | null {
  const gekuendigtZum = contract.agInfos?.gekuendigtZum;
  if (!gekuendigtZum) return null;

  const openingRuntimes = startedOpeningRuntimes(contract, today);
  if (openingRuntimes.some(({ end }) => !end)) return null;

  return openingRuntimes.reduce((latest, { end }) => (end && end > latest ? end : latest), gekuendigtZum);
}

/**
 * The last day an ended contract was actually active, the day its state should be shown for.
 *
 * That is its `gekuendigtZum` - runtimes are inclusive, so the notice day itself still counts - unless a
 * supplement was not ended together with the contract. Such a supplement opens the contract again for its own
 * runtime, so it can run past the notice date and then carries the real end.
 *
 * `null` whenever the contract is still running: a notice date in the future, none at all, or an opening
 * supplement that has not ended yet. Today is the day to show in all of those cases.
 */
export function lastActiveDayOfEndedContract(
  contract: NarrowedEntity<Contract, { agInfos: { gekuendigtZum: 1 } }> & ContractWithSupplementRuntimes,
  today: Date = todayAsCalendarDay(),
): Date | null {
  const gekuendigtZum = contract.agInfos?.gekuendigtZum;
  if (!gekuendigtZum || gekuendigtZum > today) return null;

  // only runtimes that have already begun say anything about whether the contract is over
  const openingRuntimes = startedOpeningRuntimes(contract, today);

  if (openingRuntimes.some(({ end }) => !end || end > today)) return null;

  return openingRuntimes.reduce((latest, { end }) => (end && end > latest ? end : latest), gekuendigtZum);
}
